/**
 * CessioneConfirmDialog — F-004 Phase 3 (IMPL-P3-007).
 *
 * Dialog di conferma annidato che precede il submit finale di una CESSIONE.
 * La cessione trasferisce il contratto a un nuovo inquilino: azione legale
 * significativa e sostanzialmente irreversibile → richiede una conferma
 * esplicita (WCAG 3.3.4 Error Prevention).
 *
 * Pattern di riferimento (INTRODUCTION.md Pattern 6):
 *  - `CompletionConfirmationDialog.tsx` / `UploadRicevutaModal.tsx`.
 *  - Renderizzato come sibling DENTRO il form/modal genitore a `z-[60]`
 *    (genitore a `z-50`).
 *  - ESC del genitore deve essere gated con `!confirmOpen` (lato genitore, in
 *    Phase 4) così che ESC chiuda PRIMA questo dialog interno.
 *  - Il focus torna al trigger su annulla (gestito dal genitore che ri-focussa
 *    il bottone che ha aperto il dialog; qui il focus iniziale va su "Conferma").
 *
 * Ristampa: nome/CF inquilino uscente, nome/CF nuovo inquilino, dataDecorrenza.
 * Richiede un click esplicito su "Conferma cessione" prima che il submit reale
 * parta.
 */
import * as React from 'react';
import { Button } from '@/components/ui/button';

export interface CessioneConfirmDialogProps {
  /** Mostra/nasconde il dialog interno. */
  open: boolean;
  /** Nome completo dell'inquilino uscente. */
  uscenteName: string;
  /** Codice fiscale dell'inquilino uscente. */
  uscenteCF: string;
  /** Nome completo del nuovo inquilino. */
  nuovoName: string;
  /** Codice fiscale del nuovo inquilino. */
  nuovoCF: string;
  /** Data di decorrenza (ISO `YYYY-MM-DD` o stringa già formattata). */
  dataDecorrenza: string;
  /** True mentre la mutation di submit è in flight. */
  isPending: boolean;
  /** Callback Conferma — fa partire il submit reale lato caller. */
  onConfirm: () => void;
  /** Callback Annulla — chiude il dialog senza sottomettere; il caller ri-focussa il trigger. */
  onCancel: () => void;
}

function formatDateIt(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return value;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function CessioneConfirmDialog({
  open,
  uscenteName,
  uscenteCF,
  nuovoName,
  nuovoCF,
  dataDecorrenza,
  isPending,
  onConfirm,
  onCancel,
}: CessioneConfirmDialogProps) {
  const confirmButtonRef = React.useRef<HTMLButtonElement | null>(null);

  // Focus iniziale sul Conferma quando il dialog si apre.
  React.useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      confirmButtonRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !isPending) {
          // ESC chiude PRIMA questo dialog interno; il genitore gated con
          // !confirmOpen non intercetta l'evento (stopPropagation difensivo).
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cessione-confirm-title"
        aria-describedby="cessione-confirm-desc"
        className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg"
      >
        <header className="mb-4">
          <h2 id="cessione-confirm-title" className="text-lg font-semibold tracking-tight">
            Conferma cessione del contratto
          </h2>
          <p id="cessione-confirm-desc" className="mt-1 text-sm text-muted-foreground">
            Il contratto verrà trasferito al nuovo inquilino a partire dalla data di decorrenza.
            L'azione è legalmente significativa e sostanzialmente irreversibile.
          </p>
        </header>

        <dl className="mb-4 space-y-2 rounded-md border bg-muted/20 p-3 text-sm">
          <div className="flex justify-between gap-2">
            <dt className="font-medium text-muted-foreground">Inquilino uscente</dt>
            <dd>{uscenteName}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="font-medium text-muted-foreground">CF uscente</dt>
            <dd className="font-mono">{uscenteCF}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="font-medium text-muted-foreground">Nuovo inquilino</dt>
            <dd>{nuovoName}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="font-medium text-muted-foreground">CF nuovo inquilino</dt>
            <dd className="font-mono">{nuovoCF}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="font-medium text-muted-foreground">Data decorrenza</dt>
            <dd>{formatDateIt(dataDecorrenza)}</dd>
          </div>
        </dl>

        <footer className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            Annulla
          </Button>
          <Button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            aria-disabled={isPending}
          >
            {isPending ? 'Invio in corso…' : 'Conferma cessione'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
